import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import Link from "next/link";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SearchResultItem } from "@/components/common/search-result-item";
import { useSearchDropdown } from "@/hooks/use-search-dropdown";

interface SearchDropdownProps {
  children: ReactNode;
  debouncedSearch: string;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  onClose?: () => void;
}

export function SearchDropdown({
  children,
  debouncedSearch,
  isOpen: externalIsOpen,
  setIsOpen: externalSetIsOpen,
  onClose,
}: SearchDropdownProps) {
  const {
    results,
    isLoading,
    isOpen: internalIsOpen,
    setIsOpen: internalSetIsOpen,
  } = useSearchDropdown({
    debouncedSearch,
  });

  // Use external state if provided, otherwise use internal state
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = externalSetIsOpen || internalSetIsOpen;

  const contentRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { contextSafe } = useGSAP({ scope: contentRef });
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    height: 0,
  });

  const shouldShowDropdown = debouncedSearch.length >= 2;

  // Memoize search results to prevent unnecessary re-renders
  const memoizedResults = useMemo(() => results, [results]);

  // Simple animation handler with reduced motion support
  const animateDropdown = contextSafe(
    (show: boolean, onComplete?: () => void) => {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (prefersReducedMotion) {
        onComplete?.();
        return;
      }

      if (show && contentRef.current) {
        gsap.fromTo(
          contentRef.current,
          { opacity: 0, y: -10, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.2,
            ease: "power2.out",
            onComplete,
          }
        );
      } else if (!show && contentRef.current) {
        gsap.to(contentRef.current, {
          opacity: 0,
          y: -10,
          scale: 0.95,
          duration: 0.15,
          ease: "power2.in",
          onComplete,
        });
      }
    }
  );

  // Simple results animation
  const animateResults = contextSafe(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion || !resultsRef.current?.children) {
      return;
    }

    gsap.fromTo(
      resultsRef.current.children,
      { opacity: 0, y: 10 },
      {
        opacity: 1,
        y: 0,
        duration: 0.2,
        stagger: 0.03,
        ease: "power2.out",
      }
    );
  });

  // Simple state management - always show dropdown for valid searches (2+ characters)
  useEffect(() => {
    if (shouldShowDropdown) {
      setIsOpen(true);
      animateDropdown(true);
    } else {
      setIsOpen(false);
      animateDropdown(false);
    }
  }, [shouldShowDropdown, setIsOpen, animateDropdown]);

  // Animate results when they appear
  useEffect(() => {
    if (isOpen && memoizedResults.length > 0) {
      animateResults();
    }
  }, [isOpen, memoizedResults.length, animateResults]);

  // Calculate position when dropdown opens
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    }
  }, [isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onClose?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setIsOpen, onClose]);

  return (
    <div className="relative" ref={containerRef}>
      {children}
      {shouldShowDropdown &&
        isOpen &&
        typeof window !== "undefined" &&
        createPortal(
          <div
            aria-label="Search results"
            className="fixed z-50 max-h-96 w-80 overflow-y-auto rounded-xl border-0 bg-background/95 shadow-lg backdrop-blur-sm"
            ref={contentRef}
            role="menu"
            style={{
              top: `${position.top + position.height + 8}px`,
              left: `${position.left}px`,
            }}
          >
            <div aria-live="polite">
              {/* Loading state */}
              {isLoading && (
                <div className="p-4 text-center text-muted-foreground">
                  <div className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Searching...
                </div>
              )}

              {/* Results found */}
              {!isLoading && memoizedResults.length > 0 && (
                <div ref={resultsRef}>
                  {memoizedResults.map((product) => (
                    <SearchResultItem
                      key={product.id}
                      onSelect={() => {
                        setIsOpen(false);
                        onClose?.();
                      }}
                      product={product}
                    />
                  ))}
                  <div className="mt-2 border-border/50 border-t p-3">
                    <Link
                      className="block w-full rounded-lg px-4 py-2 text-center font-heading text-muted-foreground text-sm transition-colors hover:bg-accent/50 hover:text-foreground"
                      href={`/shop?navSearch=${encodeURIComponent(debouncedSearch)}`}
                      onClick={() => {
                        setIsOpen(false);
                        onClose?.();
                      }}
                      role="menuitem"
                    >
                      View all results
                    </Link>
                  </div>
                </div>
              )}

              {/* No results found */}
              {!isLoading && memoizedResults.length === 0 && (
                <div className="p-4 text-center text-muted-foreground">
                  <div className="text-sm">No results found</div>
                  <div className="mt-1 text-xs">
                    Try searching for a different product
                  </div>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
